import { forwardRef } from 'react';
import { useInterstateTheme, useInterstateToken } from '../../InterstateThemeProvider';
import { buildVariantMap, variantTokenMap } from './variantTokenMap';
import { styled } from '@mui/material/styles';
import { deriveStyleFromToken } from './deriveStyleFromToken';
import { mergeSxStyle } from '../../common/mergeSxStyle';
// eslint-disable-next-line camelcase
import { unstable_styleFunctionSx } from '@mui/system';
import { jsx as _jsx } from "react/jsx-runtime";
const isValidTag = input => {
  try {
    return document.createElement(input).constructor.name !== 'HTMLUnknownElement';
  } catch (e) {
    return false;
  }
};
export const Typography = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    variant,
    id,
    color = 'sem.color.on-surface.default',
    children,
    tag,
    sx,
    ...rest
  } = _ref;
  const theme = useInterstateTheme();
  const getToken = useInterstateToken();
  if (!variant) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`Typography invalid variant: ${variant}`);
    }
    return null;
  }
  const variantMap = buildVariantMap(theme);
  const variantToken = variantTokenMap(variant, variantMap);
  if (!variantToken) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`Typography invalid variant not in map: ${variant}`);
    }
    return null;
  }
  const style = deriveStyleFromToken(variantToken);
  if (variant.startsWith('h1')) {
    style.fontVariationSettings = "'wdth' 64, 'GRAD' -64";
  }
  if (variant.startsWith('list')) {
    style.listStyleType = 'none';
    style[':before'] = {
      content: "'\u2022'"
    };
    switch (variant) {
      case 'list-xxs':
        {
          style[':before'] = {
            content: "'\u2022'",
            marginRight: 'calc(0.25rem * 1.5)'
          };
          break;
        }
      case 'list-xs':
        {
          style[':before'] = {
            content: "'\u2022'",
            marginRight: 'calc(0.25rem * 2)'
          };
          break;
        }
      case 'list-sm':
        {
          style[':before'] = {
            content: "'\u2022'",
            marginRight: 'calc(0.25rem * 2.5)'
          };
          break;
        }
      case 'list-md':
        {
          style[':before'] = {
            content: "'\u2022'",
            marginRight: 'calc(0.25rem * 2.5)'
          };
          break;
        }
      case 'list-lg':
        {
          style[':before'] = {
            content: "'\u2022'",
            marginRight: 'calc(0.25rem * 3)'
          };
          break;
        }
      default:
        {
          break;
        }
    }
  }
  if (color) {
    if (color.includes('color')) {
      const colorValue = getToken(color);
      if (colorValue) {
        style.color = colorValue;
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Typography: attempt to use none color token for color property: ', color);
      }
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    if (tag && !isValidTag(tag)) {
      // eslint-disable-next-line no-console
      console.error(`Typography: Invalid tag name provided in tag property: ${tag}`);
    }
  }
  const effectiveTag = tag && isValidTag(tag) ? tag : variantToken.tag;
  const effectiveStyle = mergeSxStyle(style, sx);
  const Component = styled(effectiveTag)(() => ({
    // eslint-disable-next-line camelcase
    unstable_styleFunctionSx
  }));
  return /*#__PURE__*/_jsx(Component, {
    id: id,
    ref: ref,
    ...rest,
    sx: effectiveStyle,
    "data-variant": variant,
    "data-sx": Boolean(sx),
    "data-colortoken": `${color}`,
    children: children
  });
});
Typography.displayName = 'Typography';